import { useMemo, useState } from 'react'
import { useJoinableNations } from '@/hooks/useJoinableNations'
import { AppLayout } from '@/components/layout/AppLayout'
import { NationJoinCard } from '@/components/nation/NationJoinCard'
import { NationJoinCardSkeleton } from '@/components/nation/NationJoinCardSkeleton'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { BreadcrumbItem } from '@/components/layout/Breadcrumb'

type SortOption = 'name' | 'party_count' | 'newest'

const breadcrumbs: BreadcrumbItem[] = [{ label: 'Join a Nation' }]

export default function JoinNation() {
  const { nations, isLoading, error } = useJoinableNations()

  const [selectedWorld, setSelectedWorld] = useState<string>('all')
  const [openOnly, setOpenOnly] = useState(false)
  const [sortBy, setSortBy] = useState<SortOption>('name')

  // Extract unique worlds for filter dropdown
  const worlds = useMemo(() => {
    if (!nations) return []
    const worldMap = new Map<string, { id: string; name: string }>()
    nations.forEach((n) => {
      if (!worldMap.has(n.world.id)) {
        worldMap.set(n.world.id, n.world)
      }
    })
    return Array.from(worldMap.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [nations])

  // Filter and sort nations
  const filteredNations = useMemo(() => {
    if (!nations) return []

    let result = [...nations]

    // Filter by world
    if (selectedWorld !== 'all') {
      result = result.filter((n) => n.world.id === selectedWorld)
    }

    // Filter by open only
    if (openOnly) {
      result = result.filter((n) => n.partyCount < n.nation.max_parties || n.userParty !== null)
    }

    // Sort
    switch (sortBy) {
      case 'name':
        result.sort((a, b) => a.nation.name.localeCompare(b.nation.name))
        break
      case 'party_count':
        result.sort((a, b) => a.partyCount - b.partyCount)
        break
      case 'newest':
        // Nations don't have created_at, so we use id (UUIDs are time-ordered in Supabase)
        result.sort((a, b) => b.nation.id.localeCompare(a.nation.id))
        break
    }

    return result
  }, [nations, selectedWorld, openOnly, sortBy])

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Join a Nation</h1>
          <p className="text-muted-foreground mt-2">
            Browse available nations and create a political party to start playing.
          </p>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
            Failed to load nations. Please try again later.
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Label htmlFor="world-filter" className="text-sm whitespace-nowrap">
              World:
            </Label>
            <Select value={selectedWorld} onValueChange={setSelectedWorld}>
              <SelectTrigger id="world-filter" className="w-[180px]">
                <SelectValue placeholder="All Worlds" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Worlds</SelectItem>
                {worlds.map((world) => (
                  <SelectItem key={world.id} value={world.id}>
                    {world.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="open-only"
              checked={openOnly}
              onCheckedChange={(checked) => setOpenOnly(checked === true)}
            />
            <Label htmlFor="open-only" className="text-sm cursor-pointer">
              Open only
            </Label>
          </div>

          <div className="flex items-center gap-2">
            <Label htmlFor="sort-by" className="text-sm whitespace-nowrap">
              Sort:
            </Label>
            <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortOption)}>
              <SelectTrigger id="sort-by" className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="party_count">Party Count</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <NationJoinCardSkeleton key={i} />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && filteredNations.length === 0 && (
          <div className="rounded-lg border border-dashed p-12 text-center">
            <h3 className="text-lg font-medium">No nations available</h3>
            <p className="text-muted-foreground mt-1">
              {nations && nations.length > 0
                ? 'Try adjusting your filters.'
                : 'There are no active nations to join at the moment.'}
            </p>
          </div>
        )}

        {/* Nations grid */}
        {!isLoading && !error && filteredNations.length > 0 && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filteredNations.map((item) => (
              <NationJoinCard
                key={item.nation.id}
                nation={item.nation}
                worldName={item.world.name}
                partyCount={item.partyCount}
                userParty={item.userParty}
              />
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  )
}
